"use client";

import * as React from "react";
import { useIsDark, usePrefersReducedMotion } from "../../hooks";

/**
 * Ambient motion behind the hero.
 *
 * Deliberately not a second swarm. The simulation further down the page is
 * instrumented and captioned and earns its scale claim; this is atmosphere,
 * so it carries no counter, no controls and no numbers. A few dozen motes
 * drift along a slowly evolving analytic field, each trailing its own path.
 *
 * Density is weighted toward the empty right-hand side of the hero, which
 * does two jobs at once: it fills the space the text column leaves open, and
 * it keeps the field off the headline, where any of this would read as noise
 * behind type.
 *
 * The tails are drawn from a position history rather than by fading the last
 * frame under a translucent fill. The fill is the cheaper trick and it does
 * not work: on an 8-bit canvas a trail one level above the background stays
 * there forever, so the hero slowly silts up with permanent scribble.
 */

const HISTORY = 100; // frames of tail kept per mote
const STEPS = 7; // alpha buckets per colour family

type Layer = { path: Path2D; color: string; alpha: number; width: number };

export default function HeroField() {
  const ref = React.useRef<HTMLCanvasElement | null>(null);
  const dark = useIsDark();
  const reduced = usePrefersReducedMotion();

  React.useEffect(() => {
    if (reduced) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    // Capped below the display: these are soft one-pixel trails and the extra
    // resolution buys nothing visible.
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let w = 0;
    let h = 0;
    let count = 0;
    // Left edge of the open space, measured rather than guessed. The headline
    // is wider than the paragraph under it, so the edge is a step, not a line.
    // See resize().
    let rampTop = 0;
    let rampBot = 0;
    let rampSplit = 0;

    let x = new Float32Array(0);
    let y = new Float32Array(0);
    let speed = new Float32Array(0);
    let bright = new Float32Array(0);
    /** Constant angular offset, so no two motes share a streamline. */
    let wander = new Float32Array(0);
    let life = new Float32Array(0);
    /** Ring buffer of past positions, interleaved xy, one run per mote. */
    let trail = new Float32Array(0);
    let head = 0;

    const seed = (i: number) => {
      // Seeded into the open space, so the field is populated from the first
      // frame instead of waiting for drift to carry motes across.
      const edge = Math.min(rampTop, rampBot);
      const bias = Math.random() < 0.85;
      x[i] = bias
        ? edge + Math.random() * (w - edge) * 1.2
        : edge * 0.85 + Math.random() * (w - edge * 0.85);
      y[i] = h * (0.06 + Math.random() * 0.86);
      speed[i] = 0.5 + Math.random() * 1.5;
      bright[i] = Math.random();
      wander[i] = (Math.random() - 0.5) * 1.1;
      // Short enough that motes retire before the field can gather them into
      // a bundle of parallel streamlines.
      life[i] = HISTORY * (0.9 + Math.random() * 1.5);
      // Collapse the tail onto the new position, or it draws a line across
      // the hero from wherever the mote used to be.
      const base = i * HISTORY * 2;
      for (let k = 0; k < HISTORY; k++) {
        trail[base + k * 2] = x[i];
        trail[base + k * 2 + 1] = y[i];
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // The field fills the gap the text column leaves, so take the boundary
      // from the content's own boxes rather than from a fraction of the
      // viewport. A breakpoint would be wrong at every width but one, and
      // getting it wrong means trails land on top of type.
      const section = canvas.closest("section");
      const title = section?.querySelector("h1");
      const body = section?.querySelector("p.prose-body");
      const titleBox = title?.getBoundingClientRect();
      const bodyBox = body?.getBoundingClientRect();

      rampTop = titleBox ? titleBox.right - rect.left + 26 : w * 0.62;
      rampBot = bodyBox ? bodyBox.right - rect.left + 26 : rampTop;
      rampSplit = bodyBox ? bodyBox.top - rect.top : h * 0.5;

      // Below roughly a tablet the text runs the full width and there is no
      // gap left to fill. Nothing to draw, so draw nothing.
      const open = w - Math.min(rampTop, rampBot);
      const next =
        open < 200
          ? 0
          : Math.round(Math.min(80, Math.max(18, (open * h) / 12500)));
      if (next !== count) {
        count = next;
        x = new Float32Array(count);
        y = new Float32Array(count);
        speed = new Float32Array(count);
        bright = new Float32Array(count);
        wander = new Float32Array(count);
        life = new Float32Array(count);
        trail = new Float32Array(count * HISTORY * 2);
        head = 0;
        for (let i = 0; i < count; i++) seed(i);
      }
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Palette, read from the tokens so the field tracks the theme.
    const styles = getComputedStyle(document.documentElement);
    const token = (name: string, fallback: [number, number, number]) => {
      const raw = styles.getPropertyValue(name).trim();
      const parts = raw.split(/[\s,]+/).map(Number);
      return parts.length === 3 && parts.every((n) => Number.isFinite(n))
        ? (parts as [number, number, number])
        : fallback;
    };
    const accent = token("--accent", dark ? [90, 162, 255] : [11, 98, 214]);
    const ink = token("--ink", dark ? [237, 239, 242] : [17, 19, 23]);
    const peak = dark ? 0.58 : 0.46;

    const rgb = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

    // Built once, refilled each frame. Ink is held back: on a dark ground a
    // white line carries much further than a blue one at the same alpha.
    const layers: Layer[] = [];
    for (const [color, scale] of [
      [rgb(accent), 1],
      [rgb(ink), 0.55],
    ] as const) {
      for (let s = 0; s < STEPS; s++) {
        const f = (s + 1) / STEPS;
        layers.push({
          path: new Path2D(),
          color,
          alpha: peak * scale * f,
          width: 0.7 + f * 0.7,
        });
      }
    }

    const smooth = (edge0: number, edge1: number, v: number) => {
      const t = Math.min(1, Math.max(0, (v - edge0) / (edge1 - edge0)));
      return t * t * (3 - 2 * t);
    };

    /**
     * How much of a mote is allowed through at a given point. A horizontal
     * ramp rather than a radial blob: the text column has a hard right edge
     * and so should the field.
     */
    const weight = (ax: number, ay: number) => {
      // Step from the headline's edge out to the paragraph's, eased across
      // the gap between them so the boundary reads as one shape.
      const edge =
        rampTop +
        (rampBot - rampTop) * smooth(rampSplit - 50, rampSplit + 70, ay);
      const side = smooth(edge, edge + Math.min(240, (w - edge) * 0.55), ax);
      const top = smooth(0, h * 0.16, ay);
      // Clear of the proof strip well before it, so no tail crosses a tile.
      const bottom = smooth(h * 0.82, h * 0.58, ay);
      return side * top * bottom;
    };

    let t = 0;
    let raf = 0;
    let running = true;

    const frame = () => {
      raf = requestAnimationFrame(frame);
      if (!running || !count) return;
      t += 0.006;

      ctx.clearRect(0, 0, w, h);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (let i = 0; i < layers.length; i++) layers[i].path = new Path2D();

      head = (head + 1) % HISTORY;

      for (let i = 0; i < count; i++) {
        // Smooth, slowly evolving direction field. Three sines is enough to
        // avoid a visible period and costs nothing.
        const a =
          Math.sin(x[i] * 0.0046 + t * 1.1) * 1.5 +
          Math.cos(y[i] * 0.0052 - t * 0.8) * 1.5 +
          Math.sin((x[i] + y[i]) * 0.0021 + t * 0.5) +
          wander[i];

        x[i] += Math.cos(a) * speed[i];
        y[i] += Math.sin(a) * speed[i];

        // Respawn on a timer as well as on exit: a curl field left alone
        // gathers motes into attractors, and a clump reads as a crowd, which
        // is the one thing this must not look like.
        life[i] -= 1;
        if (
          life[i] <= 0 ||
          x[i] < -40 ||
          x[i] > w + 40 ||
          y[i] < -40 ||
          y[i] > h + 40
        ) {
          seed(i);
          continue;
        }

        const base = i * HISTORY * 2;
        trail[base + head * 2] = x[i];
        trail[base + head * 2 + 1] = y[i];

        const b = bright[i];
        const family = b < 0.42 ? STEPS : 0;
        const scale = 0.45 + 0.55 * b;

        // Walk oldest to newest. Each segment picks its own bucket, so the
        // tail fades out along its length without costing a draw call.
        for (let k = 1; k < HISTORY; k++) {
          const older = (head + k) % HISTORY;
          const newer = (head + k + 1) % HISTORY;
          const ox = trail[base + older * 2];
          const oy = trail[base + older * 2 + 1];
          const nx = trail[base + newer * 2];
          const ny = trail[base + newer * 2 + 1];
          if (ox === nx && oy === ny) continue;

          const age = k / (HISTORY - 1);
          const v = weight(nx, ny) * scale * Math.pow(age, 1.35);
          if (v <= 0.05) continue;

          const step = Math.min(STEPS - 1, Math.floor(v * STEPS));
          const path = layers[family + step].path;
          path.moveTo(ox, oy);
          path.lineTo(nx, ny);
        }
      }

      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        ctx.globalAlpha = layer.alpha;
        ctx.strokeStyle = layer.color;
        ctx.lineWidth = layer.width;
        ctx.stroke(layer.path);
      }
      ctx.globalAlpha = 1;
    };

    raf = requestAnimationFrame(frame);

    // Stop paying for this the moment it cannot be seen.
    const io = new IntersectionObserver(
      (entries) => {
        running = entries[0]?.isIntersecting ?? true;
      },
      { threshold: 0 },
    );
    io.observe(canvas);

    const onVisibility = () => {
      if (document.hidden) running = false;
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dark, reduced]);

  if (reduced) return null;

  return (
    <canvas
      ref={ref}
      aria-hidden
      className="absolute inset-0 h-full w-full"
      // Fades in once the first frames have grown the tails, so the hero
      // never opens on a field of dots.
      style={{ animation: "field-in 2.4s 0.4s ease-out both" }}
    />
  );
}
